import type { AbstractLeadFormProps } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractResultItem } from "@component/assets/AbstractListingCalculator/AbstractResultItem";
import { DataBox } from "@component/assets/AbstractListingCalculator/components/DataBox";
import { MarketingAccordion } from "@component/assets/AbstractListingCalculator/components/MarketingAccordion";
import { formatText } from "@ui/format";
import { HtmlBlock, List, addParameterToURL } from "bm-ui-kit";
import LZString from "lz-string";
import type { ReactNode } from "react";
import React from "react";
import { CorporateBankAccountLeadForm } from "../LeadForm/CorporateBankAccountLeadForm";
import { getLeadCalculatorRequest, getLeadCalculatorResponse, renderFee } from "./domain";
import type {
	CorporateBankAccountRequest,
	CorporateBankAccountResponseItem,
	CorporateBankAccountSearchParams,
} from "./types";

export class CorporateBankAccountResultItem extends AbstractResultItem<
	CorporateBankAccountSearchParams,
	CorporateBankAccountRequest,
	CorporateBankAccountResponseItem
> {
	protected readonly hasDetailsBlock = false;

	protected renderProductData(): ReactNode {
		const { result } = this.props;

		return (
			<>
				<DataBox title="számlavezetési díj" value={formatText(result.hufFeeSum, "ft")} />
				<DataBox
					title="forint átutalás"
					value={renderFee(result.transactionFees.moneyTransferFees.huf.oneOffIntraBankElectronic)}
				/>
				<DataBox
					title="Deviza (SEPA) átutalás"
					value={renderFee(result.transactionFees.moneyTransferFees.sepaElectronic)}
				/>
			</>
		);
	}

	protected renderMarketingData(): ReactNode {
		const { result } = this.props;

		if (
			(!result.marketing.highlight && !result.otherInfo) ||
			(result.marketing.highlight && !result.marketing.formattedText)
		) {
			return null;
		}

		return result.marketing.highlight ? (
			<div className="marketing-block">
				<HtmlBlock className="marketing-text font-weight-light m-2" html={result.marketing.formattedText} />
			</div>
		) : (
			<MarketingAccordion
				title="Kedvezmények részletei"
				renderAccordionInDesktopMode
				onClick={(isOpen) => {
					if (isOpen) {
						this.sendResultButtonEvent("discount-details", "localStorageButton");
					}
				}}
			>
				<HtmlBlock className="marketing-text font-weight-light my-2" html={result.otherInfo} />
			</MarketingAccordion>
		);
	}

	protected renderFeedbackLead(props: AbstractLeadFormProps): ReactNode {
		const { searchParams, result } = this.props;

		return (
			<CorporateBankAccountLeadForm
				title="Kérj segítséget szakértőnktől!"
				request={getLeadCalculatorRequest(searchParams)}
				response={getLeadCalculatorResponse(result)}
				{...props}
			/>
		);
	}

	protected renderFeedbackInterest(): ReactNode {
		return (
			<>
				<div className="h4 miert-jobb">Miért jársz velünk jobban, mint a bankfiókban?</div>
				<List itemStyle="tick">
					<List.Item>
						<strong>Független szakértői csapat</strong> támogat a választásban
					</List.Item>
					<List.Item>
						<strong>Naprakész információkat,</strong> hasznos tippeket nyújtunk Neked
					</List.Item>
					<List.Item>
						Kényelmes, <strong>gyors ügyintézés</strong> bankfióki sorban állás nélkül
					</List.Item>
					<List.Item>
						<strong>Támogatunk</strong> a teljes számlanyitási folyamat alatt
					</List.Item>
				</List>
			</>
		);
	}

	protected renderFeedbackDetails(): ReactNode {
		// NOTE: Nincs részletek blokk (átirányít az adatlapra)
		return undefined;
	}

	protected getDataPageUrl(): string {
		const { result, searchParams } = this.props;

		return addParameterToURL(`/vallalati/bankszamla/adatlap/`, {
			ID: result.id,
			d: LZString.compressToEncodedURIComponent(JSON.stringify(searchParams)),
			tab: "SME_ACCOUNT",
		});
	}

	protected getComponentName(): string {
		return "CorporateBankAccountResultItem";
	}
}
